import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { JobApplication } from "./ApplicationWizard";
import {
  GuidedInterviewPrepRecord,
  GuidedInterviewPrepRequest,
  GuidedInterviewPrepTopicSection,
  GuidedInterviewPrepTopicSelection,
  jobApplicationService,
} from "../services/jobApplicationService";
import { InterviewPrepWizard } from "./InterviewPrepWizard";
import { InterviewPrepSessionDisplay } from "./InterviewPrepSessionDisplay";
import { StoredInterviewPrepsView } from "./StoredInterviewPrepsView";
import { InterviewPrepDisplay } from "./InterviewPrepDisplay";
import "./InterviewPrepPanel.css";

interface InterviewPrepPanelProps {
  isOpen: boolean;
  application: JobApplication;
  onClose: () => void;
}

type PanelTab = "new" | "stored";

const buildInitialRequestFromPrep = (
  application: JobApplication,
  prep: GuidedInterviewPrepRecord | null,
): Partial<GuidedInterviewPrepRequest> | undefined => {
  const mapTopicSections = (
    sections?: GuidedInterviewPrepTopicSection[],
  ): GuidedInterviewPrepTopicSelection[] => {
    if (!Array.isArray(sections)) {
      return [];
    }

    const topics: GuidedInterviewPrepTopicSelection[] = [];

    sections.forEach((section) => {
      const topic = String(
        section.topic || section.label || section.summary || "",
      ).trim();
      if (!topic) {
        return;
      }

      topics.push({
        topic,
        label: section.label || topic,
        description: section.summary || section.description || undefined,
        question_count: Math.min(
          10,
          Math.max(1, section.question_count || section.questions?.length || 3),
        ),
      });
    });

    return topics;
  };

  if (!prep?.input_snapshot) {
    const resultTopics = mapTopicSections(prep?.result?.topic_sections);
    if (resultTopics.length === 0) {
      return undefined;
    }

    return {
      prep_id: prep?.prep_id,
      job_application_id: application.id || "",
      company: application.company || "",
      position: application.position || "",
      job_description: application.jobSummary || application.description || "",
      position_core: application.positionCore || "",
      prep_type: prep?.prep_type || "behavioral",
      output_mode: prep?.output_mode || "model_answers",
      selected_topics: resultTopics,
      focus_areas: resultTopics.map((topic) => topic.label || topic.topic),
      live_research: false,
      use_live_research: false,
    };
  }

  const selectedTopics =
    prep.input_snapshot.selected_topics && prep.input_snapshot.selected_topics.length > 0
      ? prep.input_snapshot.selected_topics
      : prep.input_snapshot.focus_areas?.map((focusArea) => ({
          topic: focusArea,
          label: focusArea,
          question_count: 3,
        })) || [];

  return {
    prep_id: prep.prep_id,
    job_application_id: application.id || "",
    company: application.company || "",
    position: application.position || "",
    job_description: application.jobSummary || application.description || "",
    position_core: application.positionCore || "",
    ...prep.input_snapshot,
    live_research: Boolean(
      prep.input_snapshot.live_research ?? prep.input_snapshot.use_live_research,
    ),
    use_live_research: Boolean(
      prep.input_snapshot.use_live_research ?? prep.input_snapshot.live_research,
    ),
    selected_topics: selectedTopics,
    focus_areas: selectedTopics.map((topic) => topic.label || topic.topic),
  };
};

export const InterviewPrepPanel: React.FC<InterviewPrepPanelProps> = ({
  isOpen,
  application,
  onClose,
}) => {
  const [activeTab, setActiveTab] = useState<PanelTab>("new");
  const [isMounted, setIsMounted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newPrep, setNewPrep] = useState<GuidedInterviewPrepRecord | null>(null);
  const [selectedStoredPrep, setSelectedStoredPrep] =
    useState<GuidedInterviewPrepRecord | null>(null);
  const [showLegacyPrep, setShowLegacyPrep] = useState(false);
  const [wizardInitialValues, setWizardInitialValues] =
    useState<Partial<GuidedInterviewPrepRequest>>();
  const [pollingPrepId, setPollingPrepId] = useState<string | null>(null);
  const [pollingTarget, setPollingTarget] = useState<"new" | "stored" | null>(
    null,
  );
  const wizardKey = `${application.id || "new"}::${
    wizardInitialValues?.prep_id || "draft"
  }`;

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setActiveTab("new");
      setError(null);
      setNewPrep(null);
      setSelectedStoredPrep(null);
      setShowLegacyPrep(false);
      setWizardInitialValues(undefined);
      setPollingPrepId(null);
      setPollingTarget(null);
      return;
    }

    setError(null);
    setWizardInitialValues(undefined);
  }, [isOpen, application.id]);

  useEffect(() => {
    if (!isMounted || !isOpen) {
      return;
    }

    const shouldLockBodyScroll =
      typeof window !== "undefined" && window.innerWidth <= 768;
    const previousOverflow = document.body.style.overflow;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    if (shouldLockBodyScroll) {
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = previousOverflow;
    };
  }, [isMounted, isOpen, onClose]);

  useEffect(() => {
    if (!pollingPrepId || !pollingTarget) {
      return;
    }

    const intervalId = window.setInterval(async () => {
      try {
        const updatedPrep = await jobApplicationService.getStoredInterviewPrep(
          pollingPrepId,
        );
        if (!updatedPrep) {
          return;
        }

        if (pollingTarget === "new") {
          setNewPrep(updatedPrep);
        } else {
          setSelectedStoredPrep(updatedPrep);
        }

        if (updatedPrep.status !== "processing") {
          setPollingPrepId(null);
          setPollingTarget(null);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to poll interview prep");
        setPollingPrepId(null);
        setPollingTarget(null);
      }
    }, 4000);

    return () => window.clearInterval(intervalId);
  }, [pollingPrepId, pollingTarget]);

  const handleWizardComplete = useCallback(
    async (request: GuidedInterviewPrepRequest) => {
      try {
        setIsSubmitting(true);
        setError(null);
        setShowLegacyPrep(false);
        const createdPrep = await jobApplicationService.createGuidedInterviewPrep(
          request,
        );
        setNewPrep(createdPrep);
        setWizardInitialValues(undefined);

        if (createdPrep.status === "processing") {
          setPollingPrepId(createdPrep.prep_id);
          setPollingTarget("new");
        }
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to create interview prep",
        );
      } finally {
        setIsSubmitting(false);
      }
    },
    [],
  );

  const handleCreateAnotherPrep = useCallback(
    (sourcePrep: GuidedInterviewPrepRecord | null) => {
      setActiveTab("new");
      setError(null);
      setNewPrep(null);
      setShowLegacyPrep(false);
      setWizardInitialValues(buildInitialRequestFromPrep(application, sourcePrep));
    },
    [application],
  );

  const handleViewStoredPrep = useCallback(
    async (prep: GuidedInterviewPrepRecord) => {
      try {
        setError(null);
        const fullPrep =
          (await jobApplicationService.getStoredInterviewPrep(prep.prep_id)) || prep;
        setSelectedStoredPrep(fullPrep);
        setShowLegacyPrep(false);
        if (fullPrep.status === "processing") {
          setPollingPrepId(fullPrep.prep_id);
          setPollingTarget("stored");
        }
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to load interview prep",
        );
      }
    },
    [],
  );

  if (!isOpen || !isMounted || typeof document === "undefined" || !document.body) {
    return null;
  }

  const panelContent = (
    <div
      className="interview-prep-panel-overlay"
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="interview-prep-panel" onClick={(event) => event.stopPropagation()}>
        <div className="interview-prep-panel-header">
          <div className="interview-prep-panel-title">
            <h2>Interview Prep</h2>
            {application.company && application.position && (
              <p className="interview-prep-panel-subtitle">
                {application.position} at {application.company}
              </p>
            )}
          </div>
          <button
            className="interview-prep-panel-close"
            onClick={onClose}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <div className="interview-prep-panel-tabs">
          <button
            className={`interview-prep-panel-tab ${
              activeTab === "new" ? "active" : ""
            }`}
            onClick={() => {
              setActiveTab("new");
              setSelectedStoredPrep(null);
              setShowLegacyPrep(false);
            }}
          >
            New Prep
          </button>
          <button
            className={`interview-prep-panel-tab ${
              activeTab === "stored" ? "active" : ""
            }`}
            onClick={() => {
              setActiveTab("stored");
            }}
          >
            Saved Preps
          </button>
        </div>

        <div className="interview-prep-panel-content">
          {error && <div className="interview-prep-panel-error-banner">{error}</div>}

          {activeTab === "new" &&
            (newPrep ? (
              <InterviewPrepSessionDisplay
                prep={newPrep}
                onCreateAnother={() => handleCreateAnotherPrep(newPrep)}
                onEditRequest={() => handleCreateAnotherPrep(newPrep)}
              />
            ) : (
              <InterviewPrepWizard
                key={wizardKey}
                application={application}
                initialValues={wizardInitialValues}
                isSubmitting={isSubmitting}
                onComplete={handleWizardComplete}
                onCancel={onClose}
              />
            ))}

          {activeTab === "stored" && !selectedStoredPrep && !showLegacyPrep && (
            <>
              {application.interview_prep_data && (
                <div className="interview-prep-panel-legacy-card">
                  <div>
                    <h3>Legacy Prep</h3>
                    <p>
                      This job has an older interview prep saved from the previous
                      version of the feature.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setShowLegacyPrep(true);
                      setSelectedStoredPrep(null);
                    }}
                  >
                    View Legacy Prep
                  </button>
                </div>
              )}

              <StoredInterviewPrepsView
                application={application}
                onViewPrep={handleViewStoredPrep}
              />
            </>
          )}

          {activeTab === "stored" && selectedStoredPrep && (
            <InterviewPrepSessionDisplay
              prep={selectedStoredPrep}
              onBack={() => setSelectedStoredPrep(null)}
              onCreateAnother={() => handleCreateAnotherPrep(selectedStoredPrep)}
              onEditRequest={() => handleCreateAnotherPrep(selectedStoredPrep)}
            />
          )}

          {activeTab === "stored" &&
            showLegacyPrep &&
            application.interview_prep_data && (
              <div className="interview-prep-panel-legacy-view">
                <div className="interview-prep-panel-legacy-toolbar">
                  <button type="button" onClick={() => setShowLegacyPrep(false)}>
                    Back To Saved Preps
                  </button>
                  <button
                    type="button"
                    onClick={() => handleCreateAnotherPrep(null)}
                  >
                    Create New Prep
                  </button>
                </div>
                <InterviewPrepDisplay
                  interviewPrep={application.interview_prep_data}
                  companyName={application.company}
                />
              </div>
            )}
        </div>
      </div>
    </div>
  );

  return createPortal(panelContent, document.body);
};
